import moment from "moment";
import companyService from "@/services/company";
import { QUERY_KEYS, parseRange } from "@/components/report/desktop/reportQuery";
import { buildPeriodPresets, formatRange, formatShortDate } from "@/components/report/shared/periods";
import { buildStatsViews, statsKind } from "./statsViews";

const ISO = "YYYY-MM-DD";
/** Ključevi adrese kojima upravlja ljuska statistike (ostali, npr. ?ui=stari, se ne diraju). */
const MANAGED_QUERY = [QUERY_KEYS.report, QUERY_KEYS.from, QUERY_KEYS.to, QUERY_KEYS.view];

/**
 * Zajedničko stanje statistike (telefon i računar): vrsta statistike za nalog, prikazi i odeljci (grafikoni), period
 * i adresa. Restoran ima tri prikaza kao /izvestaj (podrazumevani je onaj koji odgovara većini njegovih firmi), firma
 * jedan. Ljuska dodaje raspored; adresu piše preko `writeUrl(query)`.
 */
export default {
  data() {
    const { query } = this.$route;
    const range = parseRange(query[QUERY_KEYS.from], query[QUERY_KEYS.to]);
    const today = moment().format(ISO);
    return {
      from: range ? range[0] : today,
      to: range ? range[1] : today,
      /** Dok se prikaz restorana ne odredi (učitavaju se firme), ljuska prikazuje čekanje. */
      selectedView: Number(query[QUERY_KEYS.view]) || null,
    };
  },
  computed: {
    kind() {
      return statsKind(this.$store.state.user.user?.type, this.$store.state.company);
    },
    views() {
      return buildStatsViews((key, params) => this.$t(key, params), this.kind);
    },
    view() {
      return this.views.find((view) => view.id === this.selectedView) || null;
    },
    sections() {
      return this.view ? this.view.sections : [];
    },
    menuGroups() {
      return [{ key: "stats", title: this.$t("routes.statistics"), items: this.sections }];
    },
    menuKeys() {
      return this.sections.map((section) => section.key);
    },
    currentKey() {
      const wanted = this.$route.query[QUERY_KEYS.report];
      return this.menuKeys.includes(wanted) ? wanted : this.menuKeys[0] || "";
    },
    section() {
      return this.sections.find((section) => section.key === this.currentKey) || null;
    },
    dateRange() {
      return [this.from, this.to];
    },
    presets() {
      return buildPeriodPresets();
    },
    formatLocale() {
      const config = this.getCurrentLanguageConfig();
      return config.script === "latin" && config.locale === "sr-RS" ? "sr-Latn-RS" : config.locale;
    },
    periodLong() {
      if (this.from !== this.to) {
        return formatRange(this.from, this.to);
      }
      const weekday = new Intl.DateTimeFormat(this.formatLocale, { weekday: "long" }).format(moment(this.from, ISO).toDate());
      return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${formatShortDate(this.from, true)}`;
    },
    urlQuery() {
      const query = { [QUERY_KEYS.from]: this.from, [QUERY_KEYS.to]: this.to };
      if (this.currentKey) {
        query[QUERY_KEYS.report] = this.currentKey;
      }
      if (this.selectedView && this.views.length > 1) {
        query[QUERY_KEYS.view] = String(this.selectedView);
      }
      return query;
    },
  },
  watch: {
    kind: {
      handler() {
        this.resolveView();
      },
      immediate: true,
    },
  },
  methods: {
    /** Prikaz iz adrese ako postoji, inače prvi (firma) ili onaj koji odgovara većini firmi restorana. */
    async resolveView() {
      if (!this.views.length) {
        return;
      }
      if (this.view) {
        return;
      }
      if (this.kind !== "restaurant") {
        this.selectedView = this.views[0].id;
        return;
      }
      try {
        const { data } = await companyService.getCompanies();
        const companies = data || [];
        const groupCount = companies.filter((company) => company.type === "group").length;
        this.selectedView = groupCount > companies.length - groupCount ? 2 : 1;
      } catch (error) {
        this.selectedView = 1;
      }
    },
    setView(id) {
      if (id !== this.selectedView && this.views.some((view) => view.id === id)) {
        this.selectedView = id;
      }
    },
    setPeriod({ from, to }) {
      this.from = from;
      this.to = to;
    },
    writeUrl(query) {
      const current = this.$route.query;
      const kept = Object.keys(current).reduce((acc, key) => {
        if (!MANAGED_QUERY.includes(key)) {
          acc[key] = current[key];
        }
        return acc;
      }, {});
      const next = { ...kept, ...query };
      const same =
        Object.keys(next).length === Object.keys(current).length &&
        Object.keys(next).every((key) => String(current[key]) === String(next[key]));
      if (!same) {
        this.$router.replace({ query: next }).catch(() => {});
      }
    },
  },
};
